import React, { useState } from 'react';
import { getErrorMessage } from '@/lib/utils';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Building2, ShoppingCart, Truck, ArrowRight, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { UserRole } from '../types';
import AssociationSelector from '../components/AssociationSelector';

interface Association {
  id: string;
  name: string;
  slug: string;
  logo_url?: string;
}

const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [step, setStep] = useState(1);
  const [role, setRole] = useState<UserRole | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [selectedAssociation, setSelectedAssociation] = useState<Association | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    company_name: '',
    company_type: 'producer',
    locale: 'it'
  });
  const [consents, setConsents] = useState({
    terms_accepted: false,
    marketing_accepted: false
  });
  const [loading, setLoading] = useState(false);
  const [manualLogin, setManualLogin] = useState(false);

  const { login, register } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Verifica consenso obbligatorio
    if (!isLogin && !consents.terms_accepted) {
      toast.error('Devi accettare i Termini e Condizioni per registrarti');
      return;
    }
    
    // Verifica che le password coincidano (solo in registrazione)
    if (!isLogin && formData.password !== formData.confirmPassword) {
      toast.error('Le password non coincidono');
      return;
    }
    
    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success(t('auth.loginTitle'));
        navigate('/dashboard');
      } else {
        if (role) {
          await register({ 
            ...formData, 
            role, 
            confirm_password: formData.password,
            terms_accepted: consents.terms_accepted,
            marketing_accepted: consents.marketing_accepted,
            association_id: selectedAssociation?.id
          });
          toast.success(t('auth.registerTitle'));
          navigate('/dashboard');
        }
      }
    } catch (error: any) {
      toast.error(getErrorMessage(error, "Errore durante l'operazione"));
    } finally {
      setLoading(false);
    }
  };

  const handleRoleSelect = (selectedRole: UserRole) => {
    setRole(selectedRole);
    setStep(2);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1731156679850-e73fbc21564c?w=1200"
          alt="Kitchen preparation"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-linear-to-r from-black/60 to-black/30" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <h1 className="font-heading text-5xl font-bold mb-4">OPS!</h1>
          <p className="text-xl text-white/80 max-w-md">
            Funziona davvero
          </p>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md">
          {/* Logo for mobile */}
          <div className="lg:hidden mb-8 text-center">
            <h1 className="font-heading text-3xl font-bold text-brand">OPS!</h1>
          </div>

          {isLogin ? (
            <Card className="border-border/50">
              <CardHeader className="space-y-1">
                <CardTitle className="text-2xl font-heading">{t('auth.loginTitle')}</CardTitle>
                <CardDescription>{t('auth.loginSubtitle')}</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  
                  {!manualLogin ? (
                    <>
                      {/* Accesso veloce con select */}
                      <div className="space-y-2">
                        <Label htmlFor="email">{t('auth.email')}</Label>
                        <Select
                          value={formData.email}
                          onValueChange={(value) => setFormData({ ...formData, email: value, password: '' })}
                        >
                          <SelectTrigger data-testid="login-email-select">
                            <SelectValue placeholder="Seleziona un account..." />
                          </SelectTrigger>
                      <SelectContent className="max-h-[400px]">
                        {/* Admin Sistema */}
                        <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50">Admin Sistema</div>
                        <SelectItem value="admin@horeca.it">
                          <span className="font-medium">admin@horeca.it</span>
                          <span className="text-xs text-muted-foreground ml-2">Admin Horeca</span>
                        </SelectItem>
                        <SelectItem value="admin@horecaplat.it">
                          <span className="font-medium">admin@horecaplat.it</span>
                          <span className="text-xs text-muted-foreground ml-2">Admin HORECA</span>
                        </SelectItem>
                        
                        {/* Admin Associazione */}
                        <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50 mt-2">Admin Associazione</div>
                        <SelectItem value="admin.fida@test.com">
                          <span className="font-medium">admin.fida@test.com</span>
                          <span className="text-xs text-muted-foreground ml-2">Mario Rossi - FIDA</span>
                        </SelectItem>
                        <SelectItem value="test@test.com">
                          <span className="font-medium">test@test.com</span>
                          <span className="text-xs text-muted-foreground ml-2">Test User</span>
                        </SelectItem>
                        
                        {/* Buyer + Associato */}
                        <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50 mt-2">Buyer + Associato</div>
                        <SelectItem value="buyer@fida.it">
                          <span className="font-medium">buyer@fida.it</span>
                          <span className="text-xs text-muted-foreground ml-2">Test Buyer</span>
                        </SelectItem>
                        <SelectItem value="nuovoristorante@test.com">
                          <span className="font-medium">nuovoristorante@test.com</span>
                          <span className="text-xs text-muted-foreground ml-2">Nuovo Ristorante</span>
                        </SelectItem>
                        <SelectItem value="nuovo.membro@esempio.it">
                          <span className="font-medium">nuovo.membro@esempio.it</span>
                          <span className="text-xs text-muted-foreground ml-2">Giuseppe Verdi</span>
                        </SelectItem>
                        <SelectItem value="pizzeria.napoli@test.com">
                          <span className="font-medium">pizzeria.napoli@test.com</span>
                          <span className="text-xs text-muted-foreground ml-2">Luigi Esposito</span>
                        </SelectItem>
                        <SelectItem value="ristorante5@toyou.it">
                          <span className="font-medium">ristorante5@toyou.it</span>
                          <span className="text-xs text-muted-foreground ml-2">Ristorante 5</span>
                        </SelectItem>
                        <SelectItem value="ristorante7@toyou.it">
                          <span className="font-medium">ristorante7@toyou.it</span>
                          <span className="text-xs text-muted-foreground ml-2">Gino Mario</span>
                        </SelectItem>
                        
                        {/* Solo Associato */}
                        <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50 mt-2">Solo Associato (menu limitato)</div>
                        <SelectItem value="soloassociato@test.com">
                          <span className="font-medium">soloassociato@test.com</span>
                          <span className="text-xs text-muted-foreground ml-2">Solo Associato Test</span>
                        </SelectItem>
                        
                        {/* Confcommercio Roma */}
                        <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50 mt-2">Confcommercio Roma</div>
                        <SelectItem value="admin@confcommercio.roma.it">
                          <span className="font-medium">admin@confcommercio.roma.it</span>
                          <span className="text-xs text-muted-foreground ml-2">Laura Bianchi - Admin</span>
                        </SelectItem>
                        <SelectItem value="membro@confcommercio.roma.it">
                          <span className="font-medium">membro@confcommercio.roma.it</span>
                          <span className="text-xs text-muted-foreground ml-2">Paolo Verdi - Associato</span>
                        </SelectItem>

                        {/* Buyer Standard */}
                        <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50 mt-2">Buyer Standard</div>
                        <SelectItem value="buyer@test.it">
                          <span className="font-medium">buyer@test.it</span>
                          <span className="text-xs text-muted-foreground ml-2">Luca Bianchi</span>
                        </SelectItem>
                        <SelectItem value="buyer@test.com">
                          <span className="font-medium">buyer@test.com</span>
                          <span className="text-xs text-muted-foreground ml-2">Test Buyer</span>
                        </SelectItem>
                        <SelectItem value="ristorante1@toyou.it">
                          <span className="font-medium">ristorante1@toyou.it</span>
                          <span className="text-xs text-muted-foreground ml-2">Ristorante 1</span>
                        </SelectItem>
                        <SelectItem value="ristorante3@toyou.it">
                          <span className="font-medium">ristorante3@toyou.it</span>
                          <span className="text-xs text-muted-foreground ml-2">Pinsa e buoi dei...</span>
                        </SelectItem>
                        <SelectItem value="ristorante4@toyou.it">
                          <span className="font-medium">ristorante4@toyou.it</span>
                          <span className="text-xs text-muted-foreground ml-2">L'altra Osteria</span>
                        </SelectItem>
                        <SelectItem value="settimio@toyou.it">
                          <span className="font-medium">settimio@toyou.it</span>
                          <span className="text-xs text-muted-foreground ml-2">Mario</span>
                        </SelectItem>
                        <SelectItem value="buyerlogo@test.com">
                          <span className="font-medium">buyerlogo@test.com</span>
                          <span className="text-xs text-muted-foreground ml-2">Test Buyer Logo</span>
                        </SelectItem>
                        <SelectItem value="creditbuyer@test.com">
                          <span className="font-medium">creditbuyer@test.com</span>
                          <span className="text-xs text-muted-foreground ml-2">Test Buyer Credit</span>
                        </SelectItem>
                        
                        {/* Seller / Venditore */}
                        <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50 mt-2">Seller / Venditore</div>
                        <SelectItem value="agente1@toyou.it">
                          <span className="font-medium">agente1@toyou.it</span>
                          <span className="text-xs text-muted-foreground ml-2">Agente 1</span>
                        </SelectItem>
                        <SelectItem value="produttore1@toyou.it">
                          <span className="font-medium">produttore1@toyou.it</span>
                          <span className="text-xs text-muted-foreground ml-2">Caseificio Il Buono</span>
                        </SelectItem>
                        <SelectItem value="seller@test.it">
                          <span className="font-medium">seller@test.it</span>
                          <span className="text-xs text-muted-foreground ml-2">Mario Rossi</span>
                        </SelectItem>
                        <SelectItem value="nuovo_fornitore@example.com">
                          <span className="font-medium">nuovo_fornitore@example.com</span>
                          <span className="text-xs text-muted-foreground ml-2">Giuseppe Verdi</span>
                        </SelectItem>
                        <SelectItem value="nuovo.operatore@test.it">
                          <span className="font-medium">nuovo.operatore@test.it</span>
                          <span className="text-xs text-muted-foreground ml-2">Nuovo Operatore</span>
                        </SelectItem>
                        <SelectItem value="operatore2@test.it">
                          <span className="font-medium">operatore2@test.it</span>
                          <span className="text-xs text-muted-foreground ml-2">Operatore Due</span>
                        </SelectItem>
                        <SelectItem value="operatore3@test.it">
                          <span className="font-medium">operatore3@test.it</span>
                          <span className="text-xs text-muted-foreground ml-2">Operatore Tre</span>
                        </SelectItem>
                        
                        {/* Fornitori Servizi Confcommercio */}
                        <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50 mt-2">Fornitori Servizi (Confcommercio)</div>
                        <SelectItem value="anna@bandieuro.it">
                          <span className="font-medium">anna@bandieuro.it</span>
                          <span className="text-xs text-muted-foreground ml-2">BandiEuro Consulting</span>
                        </SelectItem>
                        <SelectItem value="roberto@formaimpresa.it">
                          <span className="font-medium">roberto@formaimpresa.it</span>
                          <span className="text-xs text-muted-foreground ml-2">FormaImpresa Academy</span>
                        </SelectItem>
                        <SelectItem value="giulia@mediapress.roma.it">
                          <span className="font-medium">giulia@mediapress.roma.it</span>
                          <span className="text-xs text-muted-foreground ml-2">MediaPress Roma</span>
                        </SelectItem>
                        <SelectItem value="marco@eventirioma.it">
                          <span className="font-medium">marco@eventirioma.it</span>
                          <span className="text-xs text-muted-foreground ml-2">Eventi Roma Experience</span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-primary hover:bg-primary/90"
                    data-testid="login-submit"
                    disabled={loading || !formData.email}
                  >
                    {loading ? t('common.loading') : t('auth.login')}
                  </Button>
                  </>
                  ) : (
                    <>
                      {/* Accesso manuale con email + password */}
                      <div className="space-y-2">
                        <Label htmlFor="manual-email">{t('auth.email')}</Label>
                        <Input
                          id="manual-email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="nome@azienda.it"
                          data-testid="login-email-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="manual-password">Password</Label>
                        <div className="relative">
                          <Input
                            id="manual-password"
                            type={showPassword ? 'text' : 'password'}
                            value={formData.password}
                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                            placeholder="••••••••"
                            data-testid="login-password-input"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          >
                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                      </div>
                      <Button 
                        type="submit" 
                        className="w-full bg-primary hover:bg-primary/90"
                        data-testid="login-submit-manual"
                        disabled={loading || !formData.email || !formData.password}
                      >
                        {loading ? t('common.loading') : t('auth.login')}
                      </Button>
                    </>
                  )}
                  
                  {/* Toggle tra accesso veloce e manuale */}
                  <div className="text-center">
                    <button
                      type="button"
                      onClick={() => { setManualLogin(!manualLogin); setFormData({ ...formData, email: '', password: '' }); }}
                      className="text-sm text-primary hover:underline"
                    >
                      {manualLogin ? '← Accesso veloce (demo)' : 'Accedi con email e password →'}
                    </button>
                  </div>
                </form>
                <div className="mt-6 text-center text-sm">
                  <span className="text-muted-foreground">{t('auth.noAccount')} </span>
                  <button
                    onClick={() => { setIsLogin(false); setStep(1); }}
                    className="text-primary hover:underline font-medium"
                    data-testid="goto-register"
                  >
                    {t('auth.register')}
                  </button>
                </div>
                {/* Link legali */}
                <div className="mt-4 pt-4 border-t border-border flex justify-center gap-4 text-xs text-muted-foreground">
                  <Link to="/privacy-policy" className="hover:text-foreground">Privacy Policy</Link>
                  <span>•</span>
                  <Link to="/terms" className="hover:text-foreground">Termini e Condizioni</Link>
                  <span>•</span>
                  <Link to="/cookie-policy" className="hover:text-foreground">Cookie Policy</Link>
                </div>
              </CardContent>
            </Card>
          ) : (
            /* Register Flow */
            <>
              {step === 1 && (
                <Card className="border-border/50">
                  <CardHeader className="space-y-1">
                    <CardTitle className="text-2xl font-heading">{t('auth.registerTitle')}</CardTitle>
                    <CardDescription>{t('auth.registerSubtitle')}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <button
                      onClick={() => handleRoleSelect('buyer')}
                      data-testid="select-buyer"
                      className="w-full p-6 rounded-lg border-2 border-border hover:border-primary transition-colors text-left group"
                    >
                      <div className="flex items-center gap-4">
                        <div className="p-3 rounded-lg bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                          <ShoppingCart className="h-6 w-6" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-heading font-semibold text-lg">{t('auth.iAmBuyer')}</h3>
                          <p className="text-sm text-muted-foreground">{t('auth.buyerDesc')}</p>
                        </div>
                        <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                      </div>
                    </button>
                    <button
                      onClick={() => handleRoleSelect('seller')}
                      data-testid="select-seller"
                      className="w-full p-6 rounded-lg border-2 border-border hover:border-accent transition-colors text-left group"
                    >
                      <div className="flex items-center gap-4">
                        <div className="p-3 rounded-lg bg-accent/10 text-accent group-hover:bg-accent group-hover:text-accent-foreground transition-colors">
                          <Truck className="h-6 w-6" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-heading font-semibold text-lg">{t('auth.iAmSeller')}</h3>
                          <p className="text-sm text-muted-foreground">{t('auth.sellerDesc')}</p>
                        </div>
                        <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-accent transition-colors" />
                      </div>
                    </button>
                    <div className="mt-6 text-center text-sm">
                      <span className="text-muted-foreground">{t('auth.hasAccount')} </span>
                      <button
                        onClick={() => setIsLogin(true)}
                        className="text-primary hover:underline font-medium"
                        data-testid="goto-login"
                      >
                        {t('auth.login')}
                      </button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {step === 2 && (
                <Card className="border-border/50">
                  <CardHeader className="space-y-1">
                    <button
                      onClick={() => setStep(1)}
                      className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-2"
                    >
                      <ArrowLeft className="h-4 w-4" />
                      {t('common.back')}
                    </button>
                    <CardTitle className="text-2xl font-heading">{t('auth.registerTitle')}</CardTitle>
                    <CardDescription>
                      {role === 'buyer' ? t('auth.buyerDesc') : t('auth.sellerDesc')}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">{t('auth.yourName')}</Label>
                        <Input
                          id="name"
                          data-testid="register-name"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          required
                          placeholder="Mario Rossi"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="reg-email">{t('auth.email')}</Label>
                        <Input
                          id="reg-email"
                          type="email"
                          data-testid="register-email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          required
                          placeholder="email@esempio.com"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="reg-password">{t('auth.password')}</Label>
                        <div className="relative">
                          <Input
                            id="reg-password"
                            type={showPassword ? "text" : "password"}
                            data-testid="register-password"
                            value={formData.password}
                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                            required
                            placeholder="••••••••"
                            minLength={8}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          >
                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Minimo 8 caratteri, con maiuscola, minuscola e numero
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="reg-confirm-password">Conferma Password</Label>
                        <div className="relative">
                          <Input
                            id="reg-confirm-password"
                            type={showConfirmPassword ? "text" : "password"}
                            data-testid="register-confirm-password"
                            value={formData.confirmPassword}
                            onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                            required
                            placeholder="••••••••"
                            minLength={6}
                            className={formData.confirmPassword && formData.password !== formData.confirmPassword ? 'border-destructive' : ''}
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          >
                            {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                        {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                          <p className="text-sm text-destructive">Le password non coincidono</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="company_name">{t('auth.companyName')}</Label>
                        <Input
                          id="company_name"
                          data-testid="register-company"
                          value={formData.company_name}
                          onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                          required
                          placeholder="Nome Azienda Srl"
                        />
                      </div>
                      {role === 'seller' && (
                        <div className="space-y-2">
                          <Label htmlFor="company_type">{t('auth.companyType')}</Label>
                          <Select
                            value={formData.company_type}
                            onValueChange={(v) => setFormData({ ...formData, company_type: v })}
                          >
                            <SelectTrigger data-testid="register-company-type">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="producer">{t('directory.producer')}</SelectItem>
                              <SelectItem value="distributor">{t('directory.distributor')}</SelectItem>
                              <SelectItem value="agent">{t('directory.agent')}</SelectItem>
                              <SelectItem value="other">{t('directory.other')}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      
                      {/* Selezione Associazione (solo per buyer) */}
                      {role === 'buyer' && (
                        <AssociationSelector
                          selectedAssociation={selectedAssociation}
                          onSelect={setSelectedAssociation}
                        />
                      )}
                      
                      {/* Consensi GDPR */}
                      <div className="space-y-3 pt-2 border-t border-border">
                        {/* Consenso Termini e Condizioni - OBBLIGATORIO */}
                        <div className="flex items-start space-x-3">
                          <Checkbox
                            id="terms_accepted"
                            checked={consents.terms_accepted}
                            onCheckedChange={(checked) => 
                              setConsents({ ...consents, terms_accepted: checked === true })
                            }
                            data-testid="register-terms-checkbox"
                          />
                          <label
                            htmlFor="terms_accepted"
                            className="text-sm leading-relaxed cursor-pointer"
                          >
                            Ho letto e accetto i{' '}
                            <Link to="/terms" target="_blank" className="text-primary hover:underline">
                              Termini e Condizioni
                            </Link>
                            {' '}e l'{' '}
                            <Link to="/privacy-policy" target="_blank" className="text-primary hover:underline">
                              Informativa Privacy
                            </Link>
                            {' '}<span className="text-destructive">*</span>
                          </label>
                        </div>
                        
                        {/* Consenso Marketing - OPZIONALE */}
                        <div className="flex items-start space-x-3">
                          <Checkbox
                            id="marketing_accepted"
                            checked={consents.marketing_accepted}
                            onCheckedChange={(checked) => 
                              setConsents({ ...consents, marketing_accepted: checked === true })
                            }
                            data-testid="register-marketing-checkbox"
                          />
                          <label
                            htmlFor="marketing_accepted"
                            className="text-sm leading-relaxed cursor-pointer text-muted-foreground"
                          >
                            Acconsento a ricevere comunicazioni commerciali e promozionali via email
                          </label>
                        </div>
                      </div>
                      
                      <Button 
                        type="submit" 
                        className="w-full"
                        data-testid="register-submit"
                        disabled={loading || !consents.terms_accepted}
                      >
                        {loading ? t('common.loading') : t('auth.register')}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
